import React, { useMemo, useState } from "react";
import { Section } from "@design-system/Section/Section";
import { useWorkspaceReceiptSenders } from "@api/workspaceReceiptSender.api";
import { TablePageHeader } from "@components/pageHeader/TablePageHeader";
import { Button } from "@design-system/Button/Button";
import { useCopyToClipboard } from "@hooks/useCopyToClipboard";
import { CopyIcon } from "@icons";
import { DEFAULT_RECEIPTS_PHONE_NUMBER } from "@common/constants/domains/receipts/constants";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import { PhoneNumberSendersView } from "./PhoneNumberSendersView";
import { PhoneNumberSenderRow } from "./phoneNumberSendersViewConfig";
import { AddPhoneNumberSenderModal } from "./AddPhoneNumberSenderModal";
import { VStack } from "designSystem/VStack/VStack";
import { HStack } from "designSystem/HStack/HStack";

export const PhoneNumberSendersSection: React.FC = () => {
    const [search, setSearch] = useState("");
    const [showAddModal, setShowAddModal] = useState(false);
    const { data: senders, isLoading } = useWorkspaceReceiptSenders();
    const copyToClipboard = useCopyToClipboard();
    const { refuseIfViewOnly } = useViewOnlyRefusal();

    const tableData = useMemo<PhoneNumberSenderRow[] | null>(() => {
        if (isLoading) {
            return null;
        }
        if (!senders) {
            return [];
        }
        return senders.filter(
            (sender) =>
                sender.phoneNumber && sender.phoneNumber.includes(search),
        );
    }, [senders, isLoading, search]);

    return (
        <Section
            heading="Phone number"
            subheading={
                <VStack gap={12}>
                    <HStack gap={8}>
                        <Button
                            variant="primary"
                            rightIcon={<CopyIcon />}
                            onClick={() =>
                                copyToClipboard(DEFAULT_RECEIPTS_PHONE_NUMBER)
                            }
                        >
                            {DEFAULT_RECEIPTS_PHONE_NUMBER}
                        </Button>
                    </HStack>
                    Securely forward receipts, invoices, bills and other
                    financial documents from any approved phone number listed
                    below.
                </VStack>
            }
        >
            <TablePageHeader search={search} onSearchChange={setSearch}>
                <Button
                    variant="primary"
                    size="md"
                    onClick={() => {
                        // PRO-11488: the flow only fails on the final submit,
                        // so the refusal must happen at the click that opens it.
                        if (refuseIfViewOnly()) {
                            return;
                        }
                        setShowAddModal(true);
                    }}
                >
                    Add phone number
                </Button>
            </TablePageHeader>
            <PhoneNumberSendersView data={tableData} />

            <AddPhoneNumberSenderModal
                show={showAddModal}
                onClose={() => setShowAddModal(false)}
            />
        </Section>
    );
};
